import React, { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  FileText,
  FileCheck2,
  Gift,
  Receipt,
  Shield,
  GitBranch,
  Wallet,
  Calculator,
  Settings,
  Sparkles,
  LogOut,
  Mail,
  Plus,
  Menu,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, tid: "nav-dashboard" },
  { to: "/invoices", label: "Invoices", icon: FileText, tid: "nav-invoices" },
  { to: "/splits", label: "Splits", icon: GitBranch, tid: "nav-splits" },
  { to: "/wallet", label: "Wallet", icon: Wallet, tid: "nav-wallet" },
  { to: "/expenses", label: "Expenses", icon: Receipt, tid: "nav-expenses" },
  { to: "/tax", label: "Tax", icon: Calculator, tid: "nav-tax" },
  { to: "/filings", label: "Filings", icon: FileCheck2, tid: "nav-filings" },
  { to: "/referrals", label: "Refer & earn", icon: Gift, tid: "nav-referrals" },
  { to: "/pricing", label: "Pricing", icon: Sparkles, tid: "nav-pricing" },
  { to: "/settings", label: "Settings", icon: Settings, tid: "nav-settings" },
];

const ADMIN_NAV = [
  { to: "/admin/filings", label: "Admin queue", icon: Shield, tid: "nav-admin-filings" },
  { to: "/admin/referrals", label: "Referrals", icon: Gift, tid: "nav-admin-referrals" },
];

const SidebarContent = ({ user, logout, navigate, onNavigate }) => (
  <>
    <div className="h-16 flex items-center px-6 border-b border-[#35271e]">
      <NavLink
        to="/dashboard"
        onClick={onNavigate}
        className="font-display text-lg tracking-tight flex items-center gap-1.5 text-zinc-100"
        data-testid="brand-link"
      >
        Quill<span style={{ color: "hsl(var(--quill-warm))" }}>.</span>
      </NavLink>
    </div>

    <div className="px-3 py-4">
      <button
        onClick={() => {
          navigate("/invoices/new");
          onNavigate?.();
        }}
        data-testid="sidebar-new-invoice-btn"
        className="w-full flex items-center justify-center gap-2 text-sm font-medium py-2.5 rounded-md transition-all hover:brightness-95"
        style={{
          background: "linear-gradient(180deg, hsl(var(--quill-info)) 0%, hsl(var(--quill-warm)) 100%)",
          color: "hsl(var(--quill-ink-warm))",
        }}
      >
        <Plus size={14} strokeWidth={2.5} />
        New invoice
      </button>
    </div>

    <nav className="flex-1 px-2 space-y-0.5 overflow-y-auto">
      {NAV.map(({ to, label, icon: Icon, tid }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onNavigate}
          data-testid={tid}
          className={({ isActive }) =>
            `relative flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors ${
              isActive
                ? "text-white"
                : "text-zinc-400 hover:text-white hover:bg-[#241c17]"
            }`
          }
          style={({ isActive }) =>
            isActive
              ? {
                  background:
                    "linear-gradient(90deg, hsl(var(--quill-warm) / 0.14) 0%, hsl(var(--quill-accent) / 0.06) 100%)",
                  boxShadow: "inset 2px 0 0 hsl(var(--quill-warm))",
                }
              : undefined
          }
        >
          <Icon size={16} strokeWidth={1.5} />
          {label}
        </NavLink>
      ))}
      {user?.is_admin && (
        <>
          <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-[0.22em]" style={{ color: "hsl(var(--quill-warm))" }}>
            Admin
          </div>
          {ADMIN_NAV.map(({ to, label, icon: Icon, tid }) => (
            <NavLink
              key={to} to={to} onClick={onNavigate} data-testid={tid}
              className={({ isActive }) =>
                `relative flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors ${
                  isActive ? "text-white" : "text-zinc-400 hover:text-white hover:bg-[#241c17]"
                }`
              }
              style={({ isActive }) =>
                isActive
                  ? {
                      background: "linear-gradient(90deg, hsl(var(--quill-warm) / 0.14) 0%, hsl(var(--quill-accent) / 0.06) 100%)",
                      boxShadow: "inset 2px 0 0 hsl(var(--quill-warm))",
                    }
                  : undefined
              }
            >
              <Icon size={16} strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </>
      )}
    </nav>

    <div className="border-t border-[#35271e] p-3">
      <div className="flex items-center gap-3 px-2 py-2">
        {user?.picture ? (
          <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-[#241c17] flex items-center justify-center text-xs">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-sm truncate" data-testid="user-name">{user?.name}</div>
          <div
            className="mt-0.5 inline-flex items-center gap-1.5 max-w-full text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-sm border"
            style={{
              color: "hsl(var(--quill-accent))",
              borderColor: "hsl(var(--quill-accent) / 0.3)",
              background: "hsl(var(--quill-accent) / 0.06)",
            }}
            title={`Signed in via ${user?.auth_provider === "google" ? "Google" : "email"} · ${user?.email || ""}`}
            data-testid="signin-method-chip"
          >
            <span className="quill-pulse-dot" />
            {user?.auth_provider === "google" ? (
              <>
                <svg width="9" height="9" viewBox="0 0 48 48" aria-hidden="true" className="shrink-0">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                Google
              </>
            ) : (
              <>
                <Mail size={9} strokeWidth={2} className="shrink-0" />
                Email
              </>
            )}
          </div>
        </div>
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="text-zinc-400 hover:text-white p-2.5 -mr-1 rounded-md hover:bg-[#241c17]"
          title="Sign out"
          aria-label="Sign out"
        >
          <LogOut size={15} strokeWidth={1.5} />
        </button>
      </div>
    </div>
  </>
);

const AppLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Lock body scroll when drawer open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <div className="min-h-screen quill-tint-warm-bg text-white md:flex">
      {/* ── Mobile top bar ──────────────────────────────────────── */}
      <header
        className="md:hidden sticky top-0 z-30 h-14 flex items-center justify-between px-4 border-b border-[#35271e] backdrop-blur-md"
        style={{ background: "rgba(26, 20, 16, 0.85)" }}
        data-testid="mobile-topbar"
      >
        <NavLink to="/dashboard" className="font-display text-base tracking-tight">
          Quill<span style={{ color: "hsl(var(--quill-warm))" }}>.</span>
        </NavLink>
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          aria-label="Open menu"
          data-testid="mobile-menu-open"
          className="p-2 -mr-2 text-zinc-300 hover:text-white"
        >
          <Menu size={20} strokeWidth={1.75} />
        </button>
      </header>

      {/* ── Desktop sidebar (≥ md) ──────────────────────────────── */}
      <aside
        className="hidden md:flex w-60 shrink-0 border-r border-[#35271e] flex-col backdrop-blur-sm relative"
        style={{
          background:
            "linear-gradient(180deg, rgba(37, 27, 21, 0.92) 0%, rgba(30, 22, 17, 0.88) 100%)",
        }}
        data-testid="desktop-sidebar"
      >
        <div
          aria-hidden
          className="absolute inset-y-0 right-0 w-px pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, hsl(var(--quill-warm) / 0.5) 0%, hsl(var(--quill-accent) / 0.4) 50%, transparent 100%)",
          }}
        />
        <SidebarContent user={user} logout={logout} navigate={navigate} />
      </aside>

      {/* ── Mobile drawer ───────────────────────────────────────── */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              key="scrim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobileOpen(false)}
              className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
              data-testid="mobile-drawer-scrim"
            />
            <motion.aside
              key="drawer"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.28, ease: [0.22, 0.61, 0.36, 1] }}
              className="md:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] flex flex-col border-r border-[#35271e]"
              style={{ background: "rgba(20, 15, 12, 0.98)" }}
              data-testid="mobile-drawer"
            >
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
                data-testid="mobile-menu-close"
                className="absolute top-3 right-3 p-2 text-zinc-400 hover:text-white z-10"
              >
                <X size={18} strokeWidth={1.75} />
              </button>
              <SidebarContent
                user={user}
                logout={logout}
                navigate={navigate}
                onNavigate={() => setMobileOpen(false)}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ── Main ─────────────────────────────────────────────────── */}
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.28, ease: [0.22, 0.61, 0.36, 1] }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
};

export default AppLayout;
